/**
 * Live Pipeline Hook
 *
 * Same interface as useDemoPipeline so DashboardPage can swap seamlessly.
 * Calls real API endpoints and polls for pipeline progression.
 */

import { useState, useCallback, useRef } from "react";
import type { PipelineState } from "../types/pipeline";
import type { EventLogEntry } from "../types/metrics";
import type { InputType, NoteApiResponse } from "../types/api";
import {
  createIdempotencyKey,
  createNote,
  createNoteFromLink,
} from "../services/notes";
import { uploadAudio } from "../services/audio";
import { useNotePipeline } from "./useNotePipeline";
import { generateEvent } from "../demo/generators";

const URL_REGEX = /https?:\/\/[^\s<>"')\]]+/gi;

interface LivePipelineResult {
  pipelineState: PipelineState | null;
  events: EventLogEntry[];
  isRunning: boolean;
  isReviewing: boolean;
  noteId: string | undefined;
  /** Full note data from polling — needed by ReviewPanel for draft_summary_text/draft_tags */
  note: NoteApiResponse | undefined;
  startPipeline: (input: string, inputType: InputType) => Promise<void>;
  startAudioPipeline: (file: Blob, meta: { duration: number }) => Promise<void>;
  reset: () => void;
}

export function useLivePipeline(): LivePipelineResult {
  const [noteId, setNoteId] = useState<string | undefined>(undefined);
  const [events, setEvents] = useState<EventLogEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One text submit attempt owns one idempotency key. If the browser times out
  // after the API accepted the note, retrying the same content reuses the key
  // and the API returns the existing row instead of creating a duplicate.
  const textSubmitRef = useRef<{
    signature: string;
    idempotencyKey: string;
  } | null>(null);
  // Poll note status once we have a noteId
  // Destructure `data` (NoteApiResponse) so ReviewPanel can access draft fields
  const {
    pipelineState,
    isProcessing,
    isReviewing,
    data: note,
  } = useNotePipeline(noteId);

  const isRunning = isSubmitting || isProcessing;

  const addEvent = useCallback((event: EventLogEntry) => {
    setEvents((prev) => [event, ...prev].slice(0, 50));
  }, []);

  // Events intentionally persist across pipeline runs — the EventStreamWidget
  // shows a rolling session log, not just the current run. The 50-slot ring
  // buffer (addEvent) handles eviction of old entries.
  const reset = useCallback(() => {
    setNoteId(undefined);
    setIsSubmitting(false);
  }, []);

  const getTextIdempotencyKey = useCallback(
    (input: string, tags: string[]): string => {
      const signature = JSON.stringify({ input, tags });
      if (textSubmitRef.current?.signature !== signature) {
        // Editing the text or tags after a perceived failure is a new submit,
        // so it intentionally receives a fresh key and does not dedupe to the
        // old note.
        textSubmitRef.current = {
          signature,
          idempotencyKey: createIdempotencyKey(),
        };
      }
      return textSubmitRef.current.idempotencyKey;
    },
    [],
  );

  const ensureTextNoteAccepted = useCallback((note: NoteApiResponse): void => {
    if (note.status !== "FAILED") {
      return;
    }
    // A repeated idempotent submit can return a previously FAILED note. Clear
    // the key so the next explicit retry can create a new processing attempt.
    textSubmitRef.current = null;
    throw new Error(
      "Previous submission failed before processing; submit again to retry.",
    );
  }, []);

  const startPipeline = useCallback(
    async (input: string, inputType: InputType) => {
      reset();
      setIsSubmitting(true);

      addEvent(
        generateEvent(
          "input",
          `New ${inputType.toLowerCase()} input submitted to API`,
          "info",
        ),
      );

      try {
        let note;

        // Extract URLs from input
        const urlMatches = input.match(URL_REGEX);

        if (
          inputType === "LINK" ||
          (urlMatches && input.trim() === urlMatches[0])
        ) {
          // Pure URL input — use createNoteFromLink
          const url = urlMatches ? urlMatches[0] : input.trim();
          note = await createNoteFromLink(url);
          addEvent(
            generateEvent(
              "input",
              `Link note created — queued for extraction`,
              "info",
              note.id,
            ),
          );
        } else if (urlMatches && urlMatches.length > 0) {
          // Text with embedded URLs — create text note with source_url
          const tags = [`url:${urlMatches[0]}`];
          note = await createNote(
            input,
            tags,
            getTextIdempotencyKey(input, tags),
          );
          ensureTextNoteAccepted(note);
          textSubmitRef.current = null;
          addEvent(
            generateEvent(
              "input",
              `Text note created with ${urlMatches.length} embedded URL(s)`,
              "info",
              note.id,
            ),
          );
        } else {
          // Plain text
          const tags: string[] = [];
          note = await createNote(
            input,
            tags,
            getTextIdempotencyKey(input, tags),
          );
          ensureTextNoteAccepted(note);
          textSubmitRef.current = null;
          addEvent(
            generateEvent(
              "input",
              `Text note created — processing started`,
              "info",
              note.id,
            ),
          );
        }

        setNoteId(note.id);
        setIsSubmitting(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to submit";

        setIsSubmitting(false);
        addEvent(
          generateEvent("error", `Submission failed: ${message}`, "error"),
        );
      }
    },
    [reset, addEvent, getTextIdempotencyKey, ensureTextNoteAccepted],
  );

  const startAudioPipeline = useCallback(
    async (file: Blob, meta: { duration: number }) => {
      reset();
      setIsSubmitting(true);

      addEvent(
        generateEvent(
          "input",
          `Audio recording submitted (${meta.duration}s)`,
          "info",
        ),
      );

      try {
        const note = await uploadAudio(file, meta);
        addEvent(
          generateEvent(
            "input",
            `Audio note created — queued for Whisper STT`,
            "info",
            note.id,
          ),
        );
        setNoteId(note.id);
        setIsSubmitting(false);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to upload audio";

        setIsSubmitting(false);
        addEvent(
          generateEvent("error", `Audio upload failed: ${message}`, "error"),
        );
      }
    },
    [reset, addEvent],
  );

  return {
    pipelineState,
    events,
    isRunning,
    isReviewing: isReviewing ?? false,
    noteId,
    note,
    startPipeline,
    startAudioPipeline,
    reset,
  };
}
